import { StepProgress, StepRunState } from '@/models/StepProgress';

import { alpha, Typography } from '@mui/material';
import { Check, ErrorOutline } from '@mui/icons-material';
import classnames from 'classnames';
import Markdown from 'markdown-to-jsx';
import styled, { keyframes } from 'styled-components';

export interface StepProps {
  stepProgress: StepProgress;
}

const ICON_SX = { height: '16px', width: '16px' };
const MarkdownWithOverrides = ({ stepProgress }: StepProps) => {
  return (
    <Markdown
      options={{
        overrides: {
          span: {
            component: 'span',
            props: {
              title: stepProgress.longDescription ?? undefined,
            },
          },
          blockquote: {
            component: 'blockquote',
            props: {
              title: stepProgress.longDescription ?? undefined,
            },
          },
          ul: {
            component: 'ul',
            props: {
              title: stepProgress.longDescription ?? undefined,
            },
          },
          p: {
            component: (props) => <Typography {...props} />,
            props: { paragraph: true, variant: 'body2' },
          },
        },
      }}
    >
      {stepProgress.shortDescription || ''}
    </Markdown>
  );
};

export const Step = ({ stepProgress }: StepProps) => {
  const icon = (() => {
    switch (stepProgress.runState) {
      case StepRunState.CREATED:
      case StepRunState.RUNNING:
        return <LoadingDots />;
      case StepRunState.SUCCEEDED:
        return <Check color="secondary" sx={ICON_SX} />;
      case StepRunState.FAILED:
        return <ErrorOutline color="error" sx={ICON_SX} />;
      default:
        return null;
    }
  })();
  return (
    <Container
      className={classnames(
        'step',
        `step--state-${stepProgress.runState.toLowerCase()}`,
      )}
    >
      <MarkdownWithOverrides stepProgress={stepProgress} />
      <StatusIcon title={stepProgress.runState}>{icon}</StatusIcon>
    </Container>
  );
};

export const Container = styled.div`
  align-items: center;
  border-color: ${({ theme }) => alpha(theme.color['cream-20'].hex, 0.2)};
  border-style: solid;
  border-width: 0px 1px 1px;
  color: ${({ theme }) => theme.color['cream-100'].hex};
  display: inline-flex;
  gap: ${({ theme }) => theme.spacing(7 / 8)};
  justify-content: space-between;
  padding: 0px ${({ theme }) => theme.spacing(1.5)};

  span,
  blockquote,
  ul,
  p {
    color: ${({ theme }) => theme.color['cream-100'].hex};
    font-size: 0.875rem;
    line-height: 1.55;
  }

  &:first-of-type {
    border-top-left-radius: 4px;
    border-top-right-radius: 4px;
    border-top-width: 1px;
  }

  &:last-of-type {
    border-bottom-left-radius: 4px;
    border-bottom-right-radius: 4px;
  }

  &.step--state-created,
  &.step--state-running {
    span::after,
    blockquote::after,
    ul::after,
    p::after {
      content: '...';
    }
  }
`;

export const StatusIcon = styled.div`
  align-items: center;
  display: flex;
  justify-content: center;
`;

const LoadingDots = () => {
  return (
    <AnimatedPips>
      <Pip index="0" />
      <Pip index="1" />
      <Pip index="2" />
    </AnimatedPips>
  );
};

const Pip = ({ index }: { index: string }) => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="4"
      height="4"
      viewBox="0 0 4 4"
      fill="none"
      className={classnames('step__pip', `step__pip--index-${index}`)}
    >
      <circle cx="2" cy="2" r="2" />
    </svg>
  );
};

const pulse = keyframes`
  0% {
    fill: #faf2e9;
    opacity: .6;
  }
  40% {
    fill: #26EFAC;
    opacity: 1;
  }
  80% {
    fill: #faf2e9;
    opacity: .6;
  }
`;

export const AnimatedPips = styled.div`
  align-items: center;
  display: inline-flex;
  gap: ${({ theme }) => theme.spacing(4 / 8)};
  justify-content: center;

  .step__pip {
    animation: ${pulse} 1.5s infinite;
    fill: ${({ theme }) => theme.color['cream-60'].hex};
  }

  .step__pip--index-0 {
    animation-delay: 0s;
  }

  .step__pip--index-1 {
    animation-delay: 0.5s;
  }

  .step__pip--index-2 {
    animation-delay: 1s;
  }
`;
